import invariant from 'invariant';
import { Dispatch } from 'redux';

import { algoliaApiSearch } from 'algolia-api/algoliaApiSearch';
import { algoliaApiGetSearchConfig } from 'algolia-api/search/algoliaApiGetSearchConfig';
import getPostSearchIndex from 'algolia-api/search/getPostSearchIndex';
import { postSearchFacetQueryFilters } from 'algolia-api/search/getSearchQueryOptions';
import { QueryOptions } from 'algolia-api/types/QueryOptions';
import { fetchCmsApiAuthor } from 'modules/cms/api/cmsApiAuthorEndpoints';
import { cmsApiFetchTag } from 'modules/cms/api/cmsApiTagEndpoints';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { arrToObject } from 'utils/functional';
import { getJson } from 'utils/http/getJson';

import {
  DeselectFilterOption,
  DynamicFacet,
  MainStorePostSearchState,
  MainStorePostSearchStateResults,
  PostSearchFacetKeys,
  ResetFiltersAction,
  SearchSuccessAction,
  SelectAllFilterOption,
  SelectFilterOption,
  SetSearchFilter,
} from './postSearch';

// Utility operations on state

export function resultsFn(state: MainStorePostSearchState) {
  return state.resultsByPage[state.pageIndex.toString()];
}

const executeSearchQuery = (
  {
    state: {
      postSearch: {
        pageIndex: pageIndexState,
        query,
        searchId,
        filters,
        searchFacets,
      },
    },
    pageIndex,
    subsiteId,
    includedSubsiteIds,
    locale,
  }: {
    state: { postSearch: MainStorePostSearchState };
    pageIndex?: number;
    subsiteId: string;
    includedSubsiteIds: Array<string>;
    locale: string;
  }, // TODO: Fix this the next time the file is edited.
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> => {
  const timestamp = new Date();
  let index = getPostSearchIndex();

  // use replica that's sorted differently when query is provided by user input
  if (query && query.length > 0 && index !== null && index !== undefined) {
    index = `${index}-relevant`;
  }

  invariant(index, 'search index not retreived');
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-param-reassign
  pageIndex = pageIndex || pageIndexState;
  const searchFilters = postSearchFacetQueryFilters(
    filters,
    searchFacets,
    locale,
  );
  const subsitesToSearch = [...includedSubsiteIds, subsiteId].map(
    (id) => `subsiteId:${id}`,
  );
  const queryOptions: QueryOptions = {
    index,
    hitsPerPage: 20,
    page: pageIndex || 0,
    facets: ['*', 'subsiteId', 'pageType', 'categoryIds', 'locale'],
    facetFilters: [subsitesToSearch],
    filters: searchFilters.join(' AND '),
    attributesToSnippet: ['description:20'],
    attributesToRetrieve: ['*'],
    // TODO: list attributes,
    removeStopWords: true,
    ignorePlurals: true,
    advancedSyntax: true,
    queryLanguages: [locale],
  };
  const searchConfig = algoliaApiGetSearchConfig();
  return algoliaApiSearch({ config: searchConfig, query, queryOptions }).then(
    (results) => ({
      ...results,
      timestamp,
      searchId,
      // This is for analytics
      searchParameters: {
        query,
        queryOptions,
      },
    }),
  );
};

export function setSearchQuery(query: string, didUserChange: boolean) {
  return (dispatch: Dispatch) => {
    if (didUserChange) {
      trackEvent('Articles: Changed Search Query', {
        new_query: query, // TODO: add tracking data for POST
      });
    }

    dispatch({
      type: 'POST_SEARCH_SET_QUERY',
      query,
    });
  };
}

export function loadDynamicFacetOptions({
  authorSlug,
  subsiteId,
  tagsSlug,
}: {
  authorSlug?: string;
  subsiteId: string;
  tagsSlug?: string;
}) {
  return (
    dispatch: Dispatch,
    getState: () => { postSearch: MainStorePostSearchState },
  ) => {
    const state = getState();
    const facetsThatArentLoaded = state.postSearch.facetsThatArentLoaded.filter(
      (facet) =>
        (facet === 'articleTagsFacet' && tagsSlug) ||
        (facet === 'articleAuthorFacet' && authorSlug) ||
        facet === 'articleCategoriesFacet',
    );
    const dynamicFacets: DynamicFacet[] = [
      {
        fetchFn: () =>
          getJson(
            `/data/website/subsites/${subsiteId}/categories?lang=${CURRENT_LOCALE}`,
          ),
        facetKey: 'articleCategoriesFacet',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        responseToOptions: ({ categories }: any) => categories,
      },
    ];

    if (authorSlug) {
      dynamicFacets.push({
        fetchFn: () => fetchCmsApiAuthor(authorSlug || ''),
        facetKey: 'articleAuthorFacet',
        responseToOptions: (author) => [author],
      });
    }

    if (tagsSlug) {
      dynamicFacets.push({
        fetchFn: () => cmsApiFetchTag(tagsSlug || '', subsiteId),
        facetKey: 'articleTagsFacet',
        responseToOptions: (tag) => [tag],
      });
    }

    return Promise.all(
      dynamicFacets.map(({ facetKey, fetchFn, responseToOptions }) => {
        if (!facetsThatArentLoaded.includes(facetKey)) {
          return Promise.resolve();
        }

        return fetchFn()
          .then((response) => responseToOptions(response))
          .then((options) => {
            dispatch({
              type: 'POST_SEARCH_SET_FACET_OPTIONS',
              facetKey,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              options: options.map((f: any) => f.slug),
              optionTitles: arrToObject(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                options.map(({ slug, name }: any) => [slug, name]),
              ),
              optionsArray: options.map(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ({ slug, id, icon, parentId, name }: any) => ({
                  id,
                  name,
                  icon,
                  slug,
                  parentId,
                }),
              ),
            });
          });
      }),
    );
  };
}

export function initializeSearch({
  queryParams,
  subsiteId,
}: {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams: Record<string, any>;
  subsiteId: string;
}) {
  return (dispatch: Dispatch) =>
    Promise.all([
      dispatch({
        type: 'POST_SEARCH_INITIALIZE',
        queryParams,
      }),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        loadDynamicFacetOptions({
          subsiteId,
          authorSlug: queryParams['author.slug'],
          tagsSlug: queryParams['tags.slug'],
        }),
      ),
    ]);
}

export function uninitializeSearch() {
  return (dispatch: Dispatch) =>
    dispatch({
      type: 'POST_SEARCH_UNINITIALIZE',
    });
}

export function setPageIndex(pageIndex: number, didUserChange: boolean) {
  return (dispatch: Dispatch) => {
    if (didUserChange) {
      trackEvent('POST: Changed Search Page Number', {
        new_page: pageIndex, // TODO: tracking data
      });
    }

    dispatch({
      type: 'POST_SEARCH_SET_PAGE_INDEX',
      pageIndex,
    });
  };
}

const searchResultsSuccessful = ({
  results,
}: {
  results: MainStorePostSearchStateResults;
}): SearchSuccessAction => ({
  type: 'POST_SEARCH_RESULTS_SUCCESS',
  results,
});

export function search(
  subsiteId: string,
  includedSubsiteIds: Array<string>,
  locale: string,
) {
  return (
    dispatch: Dispatch,
    getState: () => { postSearch: MainStorePostSearchState },
  ) => {
    dispatch({
      type: 'POST_SEARCH_STARTED',
    });
    executeSearchQuery({
      state: getState(),
      subsiteId,
      includedSubsiteIds,
      locale,
    }).then((results) => {
      dispatch(
        searchResultsSuccessful({
          results,
        }),
      );
    });
  };
}

export function setSearchFilter(
  filterKey: string,
  value: string | null | undefined,
): SetSearchFilter {
  if (filterKey === 'type') {
    invariant(value, 'type can never be set to undefined/null');
  }

  return {
    type: 'POST_SEARCH_SET_FILTER',
    key: filterKey,
    value,
  };
}

export function selectSearchFilterOption(
  filterKey: string,
  option: string | null | undefined,
): SelectFilterOption {
  return {
    type: 'POST_SEARCH_SELECT_FILTER_OPTION',
    key: filterKey,
    option,
  };
}

export function deselectSearchFilterOption(
  filterKey: string,
  option: string | null | undefined,
): DeselectFilterOption {
  return {
    type: 'POST_SEARCH_DESELECT_FILTER_OPTION',
    key: filterKey,
    option,
  };
}

export function selectAllSearchFilterOptions(
  filterKey: PostSearchFacetKeys,
): SelectAllFilterOption {
  return {
    type: 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS',
    key: filterKey,
  };
}

export function resetSearchFilters(): ResetFiltersAction {
  return {
    type: 'POST_SEARCH_RESET_FILTERS',
  };
}
